import { FunctionComponent } from 'react';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import ArrowsButtonDownMin from '@/components/atom/icon/ArrowsButtonDownMin';
import QnaStickyWriteButton from '@/components/common/QnaStickyWriteButton';
import CategoryBottomSheet from '@/components/common/communication/qna/CategoryBottomSheet';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import AiNotificationBanner from '@/components/templates/mobile/communication//Common/BoardTemplate/QnaHome/AiNotificationBanner';
import useQnaRealtimeViewModel from '@/hooks/qna/list/useQnaRealtimeViewModel';
import useScreenFocus from '@/hooks/useScreenFocus';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';

import AppInstallTopBanner from '@/components/templates/mobile/communication/Common/AppInstallTopBanner';
import RealtimeWebQna from '@/features/qna/realtime-web/ui/RealtimeWebQna';
import RealtimeWebviewQna from '@/features/qna/realtime-webview/ui/RealtimeWebviewQna';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';

interface Props extends CustomPageProps {
  viewModel: ReturnType<typeof useQnaRealtimeViewModel>;
}
const QnaRealtimeTemplate: FunctionComponent<Props> = ({ client, viewModel }) => {
  useScreenFocus();

  const topBannerViewModel = useAppInstallTopBanner();

  return (
    <TemplateLayout.Wrapper>
      {client.clientType === ClientType.WEB && (
        <BreadCrumbsWrapper>
          <BreadCrumbs
            visible
            name={viewModel.selectedCategoriesBottomSheetTabItem?.title ?? '전체'}
            path={`${ROUTES.QNA.REALTIME.path}`}
            type={'qna'}
            urlParameter={
              viewModel.selectedCategoriesBottomSheetTabItem?.value
                ? `?categoryId=${viewModel.selectedCategoriesBottomSheetTabItem?.value}`
                : ''
            }
          />
        </BreadCrumbsWrapper>
      )}
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title={'실시간 의료상담'} />
        </TemplateLayout.Header>
      )}
      {client.clientType === ClientType.MOBILE_WEB && topBannerViewModel.isVisible && (
        <AppInstallTopBanner
          content='web_top_bnr_list'
          onClickClose={topBannerViewModel.hideBanner}
          trackingInfo={{
            campaign: 'web_content_qna',
          }}
        />
      )}
      <TemplateLayout.Content>
        <CategoryWrapper>
          {viewModel.selectedCategoriesBottomSheetTabItem?.title ?? '전체'}
          <CategoryButton onClick={viewModel.onClickCategoriesBottomSheetTrigger}>
            증상/과목 선택
            <ArrowsButtonDownMin size={12} />
          </CategoryButton>
        </CategoryWrapper>
        <AiNotificationBanner />
        {client.isWebView ? (
          <RealtimeWebviewQna onClickQuestion={viewModel.onClickQuestion} pageName={viewModel.pageName} />
        ) : (
          <RealtimeWebQna />
        )}
        <QnaStickyWriteButton
          buttonText={viewModel.bottomButtonText}
          pageName={viewModel.pageName}
          onClickButton={viewModel.onClickStickButton}
        />

        <CategoryBottomSheet
          isVisible={viewModel.showCategoriesBottomSheet}
          toggleBottomSheet={viewModel.toggleCategoriesBottomSheet}
          onClickCategoryItem={viewModel.onClickCategoriesBottomSheetTabListItem}
          categoryGroup={viewModel.categoryGroup}
          selectedTab={viewModel.selectedTab}
          onClickCategoriesBottomSheetTab={viewModel.onClickCategoriesBottomSheetTab}
          getSelectedTabItemList={viewModel.getSelectedTabItemList}
        />
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default QnaRealtimeTemplate;

const BreadCrumbsWrapper = styled.div`
  padding: 26px 24px 0 24px;
`;

const CategoryWrapper = styled.div`
  display: flex;
  padding: 16px 24px 0px;

  ${({ theme }) => `
    ${theme.typography.UI22SB};
    color: ${theme.color.gray900};
  `};
`;

const CategoryButton = styled(DefaultButtonStyled)`
  padding: 6px 12px;
  border-radius: 8px;
  margin-left: 12px;

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray600};
    background: ${theme.color.gray50};
    
    &: hover {
      background: ${theme.color.gray100};
    }
    &: active {
      background: ${theme.color.gray200};
    }
  `};
`;
